import React from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import { CloudUpload, MenuBook } from '@mui/icons-material';
import { useMenuStore } from '../../stores/menuStore.js';

/**
 * Mobile Menus tab — open, select, and upload menus.
 */
export const MobileMenusTab = () => {
  const { menus, selectedMenuId, setSelectedMenu, isLoading } = useMenuStore();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 6 }}>
        <CircularProgress size={28} sx={{ color: 'text.secondary' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
      {/* Header */}
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 2 }}>
        Menu Management
      </Typography>

      {/* Menu selector */}
      {menus.length > 0 ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
            Active Menus
          </Typography>
          <Select
            value={selectedMenuId || ''}
            onChange={(e) => setSelectedMenu(e.target.value)}
            size="small"
            fullWidth
            displayEmpty
            sx={{
              borderRadius: '14px',
              bgcolor: 'rgba(255, 255, 255, 0.72)',
              fontSize: '0.88rem',
              fontWeight: 600,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C8C8CC' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#B0B0B5' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                bordercolor: 'primary.main',
                borderWidth: 2,
              },
            }}
          >
            {menus.map((menu) => (
              <MenuItem key={menu.menu_id} value={menu.menu_id}>
                {menu.menu_name}
                {menu.is_primary_menu === 'yes' && ' ★'}
              </MenuItem>
            ))}
          </Select>
        </Box>
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            bgcolor: 'rgba(255, 255, 255, 0.72)',
            borderRadius: '18px',
            border: '1px solid #C8C8CC',
            mb: 3,
          }}
        >
          <MenuBook sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 500 }}>
            No menus available
          </Typography>
        </Box>
      )}

      {/* Menu list cards */}
      {menus.map((menu) => (
        <Box
          key={menu.menu_id}
          onClick={() => setSelectedMenu(menu.menu_id)}
          sx={{
            p: 2,
            mb: 1.5,
            bgcolor: menu.menu_id === selectedMenuId ? 'rgba(69, 39, 160, 0.08)' : 'rgba(255, 255, 255, 0.72)',
            borderRadius: '14px',
            border: '1px solid',
            borderColor: menu.menu_id === selectedMenuId ? '#4527A0' : '#C8C8CC',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:active': { transform: 'scale(0.98)' },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.92rem' }}>
                {menu.menu_name}
              </Typography>
              {menu.is_primary_menu === 'yes' && (
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  Primary Menu
                </Typography>
              )}
            </Box>
            {menu.menu_id === selectedMenuId && (
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />
            )}
          </Box>
        </Box>
      ))}

      {/* Upload menu button */}
      <Button
        fullWidth
        variant="outlined"
        startIcon={<CloudUpload />}
        sx={{
          mt: 3,
          borderRadius: '14px',
          py: 1.5,
          fontWeight: 600,
          fontSize: '0.88rem',
          bordercolor: 'primary.main',
          color: 'primary.main',
          textTransform: 'none',
          '&:hover': { bgcolor: 'rgba(69, 39, 160, 0.04)' },
        }}
      >
        Upload New Menu
      </Button>
    </Box>
  );
};
